import { SepoCategory, SepoTagType, ApiLinkEntity, ApiLinkRel, ApiServiceName } from "config/constants"
import { IApiLink, IMultipleEntitiesResponse, IMultipleEntities, Guid } from "@/types"
import ApiService from "services/ApiService"
import { hasMorePages } from "services/utils"

export interface IGetSepoTagType {
    id: Guid
    category: SepoCategory
    tagType: SepoTagType
    allowedParentTagTypeId?: string | null
    getTagsLink?: IApiLink | null
}

export interface IGetSepoTag {
    value: string
}

type Constructable = new (...args: any[]) => object;

export default function ExtendAsSelfPopulatable<BC extends Constructable>(Base: BC, apiServiceName: ApiServiceName) {
    return class extends Base {

        #sepoTagTypesCache: IGetSepoTagType[] | null = null
        #sepoGetTagsLinkCache: IApiLink | null = null

        async getSepoTags(params: {
            category: SepoCategory,
            tagType: SepoTagType,
            search?: string,
            parentValue?: string,
            pageNumber?: number
        }): Promise<IMultipleEntities<string> | null> {
            const {
                category,
                tagType,
                search,
                parentValue,
                pageNumber
            } = params

            if (!this.#sepoTagTypesCache || !this.#sepoGetTagsLinkCache) {
                let sepoTagTypesResp: IMultipleEntitiesResponse<IGetSepoTagType>
                try {
                    sepoTagTypesResp = await ApiService.request({
                        serviceName: apiServiceName,
                        followLink: {
                            entity: ApiLinkEntity.sepoTagType,
                            rel: ApiLinkRel.all,
                            query: {
                                PageNumber: 1,
                                PageSize: 50
                            }
                        }
                    })
                } catch (e) {
                    // match the getTags call below: this loader returns null on
                    // failure rather than throwing (it now matters because a
                    // non-2xx response rejects instead of resolving)
                    return null
                }

                this.#sepoTagTypesCache = sepoTagTypesResp.entities
                this.#sepoGetTagsLinkCache = sepoTagTypesResp.links?.find(lnk => lnk.entity === ApiLinkEntity.sepoTag && lnk.rel === ApiLinkRel.all) ||  null
            }

            if (!this.#sepoTagTypesCache) {
                return null
            }

            const sepoTagTypes = this.#sepoTagTypesCache
            const sepoGetTagsLink = this.#sepoGetTagsLinkCache

            const tagTypeId = sepoTagTypes.find(tt => tt.category === category && tt.tagType === tagType)?.id

            if (!tagTypeId || !sepoGetTagsLink) {
                return null
            }

            const query: any = {
                TagTypeId: tagTypeId,
                PageNumber: pageNumber || 1,
                PageSize: 10,
            }

            if (parentValue) {
                query.ParentTagValue = parentValue
            }

            if (search) {
                query.TagValue = search
            }

            let sepoTagsResp: IMultipleEntitiesResponse<IGetSepoTag>

            try {
                sepoTagsResp = await ApiService.followLink({
                    link: sepoGetTagsLink,
                    linkParams: { query }
                })
            } catch(e) {
                return null
            }

            return {
                list: sepoTagsResp.entities.map(e => e.value),
                pagination: sepoTagsResp.paginationMetadata,
                loadMore: hasMorePages(sepoTagsResp.paginationMetadata) ? () => this.getSepoTags({...params, pageNumber: sepoTagsResp.paginationMetadata!.currentPage + 1}) : undefined,
            }
        }

    }
}
